import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, Modal } from "../components/ui";

const TIPO_APPARECCHIATURA_LABELS = {
  100000000: "Ascensore/Montacarichi",
  100000001: "Caldaia/Centrale termica",
  100000002: "Climatizzazione",
  100000003: "Impianto elettrico",
  100000004: "Impianto antincendio",
  100000005: "Impianto gas medicali",
  100000006: "Gruppo elettrogeno",
  100000007: "Autoclave/Sterilizzatrice",
  100000008: "Apparecchiatura medicale",
  100000009: "Impianto idrico/Sanitario",
  100000011: "Cancello/Porta automatica",
  100000012: "Sistema di sicurezza",
  100000013: "Cucina industriale",
  100000014: "Lavanderia industriale",
  100000099: "Altro",
};

const TIPO_ICONS = {
  100000000: "🛗",
  100000001: "🔥",
  100000002: "❄️",
  100000003: "⚡",
  100000004: "🧯",
  100000005: "🏥",
  100000006: "🔌",
  100000007: "🧫",
  100000008: "💉",
  100000009: "🚿",
  100000011: "🚪",
  100000012: "🔒",
  100000013: "🍳",
  100000014: "🧺",
  100000099: "📦",
};

const STATO_OPTIONS = {
  100000000: { label: "Funzionante", variant: "success", icon: "✅" },
  100000001: { label: "Guasta", variant: "danger", icon: "❌" },
  100000002: { label: "In manutenzione", variant: "warning", icon: "🔧" },
  100000003: { label: "Dismessa", variant: "default", icon: "🚫" },
};

const TIPO_AREA_LABELS = {
  100000000: "Ala",
  100000001: "Piano",
  100000002: "Nucleo",
  100000003: "Stanza",
};

function DettaglioApparecchiaturaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const [apparecchiatura, setApparecchiatura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    nome: "",
    tipoDocumento: "",
    dataDocumento: "",
    dataScadenza: "",
    descrizione: "",
  });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [loadingAvailableDocs, setLoadingAvailableDocs] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const canEdit = isAdmin || isRspp;
  const canDelete = isAdmin;
  const canUploadDoc = isAdmin || isRspp || isDirezione;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await apiRequest(`/apparecchiature/${id}`);
        setApparecchiatura(data);

        // Carica tipologie intervento associate
        const tipData = await apiRequest(
          `/apparecchiature/${id}/tipologie-intervento`,
        ).catch(() => ({ value: [] }));
        setTipologieIntervento(tipData.value || []);

        // Carica documenti associati
        const docData = await apiRequest(
          `/apparecchiature/${id}/documenti`,
        ).catch(() => ({ value: [] }));
        setDocumenti(docData.value || []);
      } catch (error) {
        console.error("Error fetching apparecchiatura:", error);
        showToast("Errore nel caricamento dell'apparecchiatura", "error");
        navigate("/apparecchiature");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, apiRequest, navigate, showToast]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiRequest(`/apparecchiature/${id}`, { method: "DELETE" });
      showToast("Apparecchiatura eliminata con successo", "success");
      navigate("/apparecchiature");
    } catch (error) {
      console.error("Error deleting apparecchiatura:", error);
      showToast(error.message || "Errore nell'eliminazione", "error");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("it-IT");
  };

  const getManutenzioneStatus = () => {
    if (!apparecchiatura?.dataProssimaManutenzione) return null;
    const data = new Date(apparecchiatura.dataProssimaManutenzione);
    const oggi = new Date();
    const diffDays = Math.ceil((data - oggi) / (1000 * 60 * 60 * 24));

    if (diffDays < 0)
      return { label: "Scaduta", variant: "danger", days: Math.abs(diffDays) };
    if (diffDays <= 30)
      return { label: "In scadenza", variant: "warning", days: diffDays };
    return { label: "OK", variant: "success", days: diffDays };
  };

  const TIPO_DOCUMENTO_LABELS = {
    100000021: "Dichiarazione CE",
    100000022: "Libretto uso e manutenzione",
    100000023: "Scheda tecnica apparecchiatura",
    100000024: "Certificato collaudo",
    100000025: "Verbale verifica periodica",
    100000003: "Rapportino manutenzione",
    100000005: "Certificazione",
    100000011: "Altro",
    100000014: "Dichiarazione di conformita",
    100000015: "Documentazione impianti",
  };

  const TIPI_DOC_APPARECCHIATURE = [
    { value: 100000021, label: "Dichiarazione CE" },
    { value: 100000022, label: "Libretto uso e manutenzione" },
    { value: 100000023, label: "Scheda tecnica apparecchiatura" },
    { value: 100000024, label: "Certificato collaudo" },
    { value: 100000025, label: "Verbale verifica periodica" },
    { value: 100000011, label: "Altro" },
  ];

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.nome || !uploadForm.tipoDocumento) {
      showToast("Compila nome, tipo documento e seleziona un file", "error");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", uploadForm.file);
      formDataUpload.append("nome", uploadForm.nome);
      formDataUpload.append("tipoDocumento", uploadForm.tipoDocumento);
      formDataUpload.append("rsaId", apparecchiatura.rsaId);
      formDataUpload.append("apparecchiaturaIds", id);
      if (uploadForm.dataDocumento)
        formDataUpload.append("dataDocumento", uploadForm.dataDocumento);
      if (uploadForm.dataScadenza)
        formDataUpload.append("dataScadenza", uploadForm.dataScadenza);
      if (uploadForm.descrizione)
        formDataUpload.append("descrizione", uploadForm.descrizione);

      await apiRequest("/documenti/upload", {
        method: "POST",
        body: formDataUpload,
        isFormData: true,
      });

      showToast("Documento caricato con successo", "success");
      setShowUploadModal(false);
      setUploadForm({
        file: null,
        nome: "",
        tipoDocumento: "",
        dataDocumento: "",
        dataScadenza: "",
        descrizione: "",
      });

      // Ricarica documenti
      const docData = await apiRequest(
        `/apparecchiature/${id}/documenti`,
      ).catch(() => ({ value: [] }));
      setDocumenti(docData.value || []);
    } catch (error) {
      console.error("Upload error:", error);
      showToast(error.message || "Errore nel caricamento", "error");
    } finally {
      setUploading(false);
    }
  };

  const openLinkModal = async () => {
    setShowLinkModal(true);
    setLinkSearch("");
    setLoadingAvailableDocs(true);
    try {
      const data = await apiRequest(
        `/documenti?rsaId=${apparecchiatura.rsaId}`,
      );
      // Escludi documenti già associati
      const associatedIds = new Set(documenti.map((d) => d.id));
      setAvailableDocs(
        (data.value || []).filter((d) => !associatedIds.has(d.id)),
      );
    } catch (error) {
      showToast("Errore nel caricamento documenti", "error");
    } finally {
      setLoadingAvailableDocs(false);
    }
  };

  const handleLinkDocument = async (docId) => {
    try {
      await apiRequest(`/apparecchiature/${id}/documenti/${docId}`, {
        method: "POST",
      });
      showToast("Documento collegato con successo", "success");
      // Ricarica documenti associati
      const docData = await apiRequest(
        `/apparecchiature/${id}/documenti`,
      ).catch(() => ({ value: [] }));
      setDocumenti(docData.value || []);
      setShowLinkModal(false);
    } catch (error) {
      showToast(error.message || "Errore nel collegamento", "error");
    }
  };

  const handleDisassociateDoc = async (docId) => {
    if (
      !confirm(
        "Rimuovere il collegamento di questo documento dall'apparecchiatura?",
      )
    )
      return;
    try {
      await apiRequest(`/apparecchiature/${id}/documenti/${docId}`, {
        method: "DELETE",
      });
      setDocumenti((prev) => prev.filter((d) => d.id !== docId));
      showToast("Documento scollegato", "success");
    } catch (error) {
      showToast(error.message || "Errore", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!apparecchiatura) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Apparecchiatura non trovata</p>
      </div>
    );
  }

  const statoInfo = STATO_OPTIONS[apparecchiatura.stato] || {
    label: "Sconosciuto",
    variant: "default",
    icon: "❓",
  };
  const manutenzioneStatus = getManutenzioneStatus();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mt-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">
                {TIPO_ICONS[apparecchiatura.tipoApparecchiatura] || "📦"}
              </span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {apparecchiatura.nome}
                </h1>
                <p className="text-gray-500">
                  {apparecchiatura.tipoApparecchiaturaLabel}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant={statoInfo.variant}>
                {statoInfo.icon} {statoInfo.label}
              </Badge>
              {!apparecchiatura.attivo && (
                <Badge variant="default">Inattiva</Badge>
              )}
              {manutenzioneStatus &&
                manutenzioneStatus.variant !== "success" && (
                  <Badge variant={manutenzioneStatus.variant}>
                    Manutenzione {manutenzioneStatus.label} (
                    {manutenzioneStatus.days}gg)
                  </Badge>
                )}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/apparecchiature/${id}/modifica`)}
            >
              Modifica
            </Button>
            {canDelete && (
              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                Elimina
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonna sinistra */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informazioni Generali */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informazioni Generali
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Marca</p>
                <p className="font-medium">{apparecchiatura.marca || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Modello</p>
                <p className="font-medium">{apparecchiatura.modello || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Matricola Interna</p>
                <p className="font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                  {apparecchiatura.matricolaInterna || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Numero di Serie (S/N)</p>
                <p className="font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                  {apparecchiatura.codiceSeriale || "-"}
                </p>
              </div>
            </div>
          </Card>

          {/* Ubicazione */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Ubicazione
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">RSA</p>
                <Link
                  to={`/rsa/${apparecchiatura.rsaId}`}
                  className="text-primary-600 hover:underline font-medium"
                >
                  {apparecchiatura.rsaNome}
                </Link>
              </div>
              <div>
                <p className="text-sm text-gray-500">Immobile</p>
                {apparecchiatura.immobileId ? (
                  <Link
                    to={`/immobili/${apparecchiatura.immobileId}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {apparecchiatura.immobileNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">Non specificato</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Area / Stanza</p>
                {apparecchiatura.ubicazioneId ? (
                  <Link
                    to={`/aree/${apparecchiatura.ubicazioneId}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {TIPO_AREA_LABELS[apparecchiatura.ubicazioneTipo]}:{" "}
                    {apparecchiatura.ubicazioneNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">Non specificata</p>
                )}
              </div>
            </div>
          </Card>

          {/* Date e Manutenzione */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Date e Manutenzione
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Installazione</p>
                <p className="font-medium">
                  {formatDate(apparecchiatura.dataInstallazione)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Scadenza Garanzia</p>
                <p className="font-medium">
                  {formatDate(apparecchiatura.dataScadenzaGaranzia)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Ultima Manutenzione</p>
                <p className="font-medium">
                  {formatDate(apparecchiatura.dataUltimaManutenzione)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Prossima Manutenzione</p>
                <p
                  className={`font-medium ${manutenzioneStatus?.variant === "danger" ? "text-red-600" : manutenzioneStatus?.variant === "warning" ? "text-orange-600" : ""}`}
                >
                  {formatDate(apparecchiatura.dataProssimaManutenzione)}
                  {manutenzioneStatus &&
                    manutenzioneStatus.variant !== "success" &&
                    " ⚠️"}
                </p>
              </div>
            </div>
          </Card>

          {/* Note */}
          {apparecchiatura.note && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {apparecchiatura.note}
              </p>
            </Card>
          )}
        </div>

        {/* Colonna destra */}
        <div className="space-y-6">
          {/* Fornitori */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Fornitori
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Fornitore Acquisto</p>
                {apparecchiatura.fornitoreAcquistoId ? (
                  <Link
                    to={`/fornitori/${apparecchiatura.fornitoreAcquistoId}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {apparecchiatura.fornitoreAcquistoNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">Non specificato</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Fornitore Manutenzione</p>
                {apparecchiatura.fornitoreManutenzioneId ? (
                  <Link
                    to={`/fornitori/${apparecchiatura.fornitoreManutenzioneId}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {apparecchiatura.fornitoreManutenzioneNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">Non specificato</p>
                )}
              </div>
            </div>
          </Card>

          {/* Documenti */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Documenti</h2>
              {canUploadDoc && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={openLinkModal}
                    className="text-sm text-gray-500 hover:text-primary-600 font-medium"
                    title="Collega documento esistente"
                  >
                    <svg
                      className="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    Collega
                  </button>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    + Carica nuovo
                  </button>
                </div>
              )}
            </div>

            {/* Flag rapidi */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={
                    apparecchiatura.certificatoConformita
                      ? "text-green-500"
                      : "text-gray-300"
                  }
                >
                  {apparecchiatura.certificatoConformita ? "✓" : "○"}
                </span>
                <span
                  className={
                    apparecchiatura.certificatoConformita
                      ? "text-gray-900"
                      : "text-gray-400"
                  }
                >
                  Certificato CE
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={
                    apparecchiatura.librettoManutenzione
                      ? "text-green-500"
                      : "text-gray-300"
                  }
                >
                  {apparecchiatura.librettoManutenzione ? "✓" : "○"}
                </span>
                <span
                  className={
                    apparecchiatura.librettoManutenzione
                      ? "text-gray-900"
                      : "text-gray-400"
                  }
                >
                  Libretto Uso e Manutenzione
                </span>
              </div>
            </div>

            {/* Lista documenti associati */}
            {documenti.length > 0 ? (
              <div className="border-t pt-3 space-y-2">
                {documenti.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between group py-1"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="min-w-0">
                        {doc.urlSharepoint ? (
                          <a
                            href={doc.urlSharepoint}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:underline truncate block"
                          >
                            {doc.nome}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-900 truncate block">
                            {doc.nome}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {TIPO_DOCUMENTO_LABELS[doc.tipoDocumento] ||
                            "Documento"}
                        </span>
                      </div>
                    </div>
                    {canUploadDoc && (
                      <button
                        onClick={() => handleDisassociateDoc(doc.id)}
                        className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Rimuovi collegamento"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 border-t pt-3">
                Nessun documento associato
              </p>
            )}
          </Card>

          {/* Tipologie Intervento Associate */}
          {tipologieIntervento.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Tipologie Intervento
              </h2>
              <ul className="space-y-2">
                {tipologieIntervento.map((tip) => (
                  <li key={tip.id} className="text-sm">
                    <span className="font-medium">{tip.nome}</span>
                    {tip.frequenzaValore && (
                      <span className="text-gray-500 ml-2">
                        (ogni {tip.frequenzaValore}{" "}
                        {tip.frequenzaUnita === 100000000
                          ? "giorni"
                          : tip.frequenzaUnita === 100000001
                            ? "mesi"
                            : "anni"}
                        )
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Metadata */}
          <Card className="p-6 bg-gray-50">
            <h2 className="text-sm font-medium text-gray-500 mb-3">
              Informazioni Sistema
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">ID</span>
                <span className="font-mono text-xs">{apparecchiatura.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Creato</span>
                <span>{formatDate(apparecchiatura.createdOn)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Modificato</span>
                <span>{formatDate(apparecchiatura.modifiedOn)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Upload Document Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Carica documento"
      >
        <form onSubmit={handleUploadDoc} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File *
            </label>
            <input
              type="file"
              onChange={(e) =>
                setUploadForm((prev) => ({ ...prev, file: e.target.files[0] }))
              }
              className="w-full text-sm"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome documento *
            </label>
            <input
              type="text"
              value={uploadForm.nome}
              onChange={(e) =>
                setUploadForm((prev) => ({ ...prev, nome: e.target.value }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="es. Dichiarazione CE Ascensore"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo documento *
            </label>
            <select
              value={uploadForm.tipoDocumento}
              onChange={(e) =>
                setUploadForm((prev) => ({
                  ...prev,
                  tipoDocumento: e.target.value,
                }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Seleziona tipo...</option>
              {TIPI_DOC_APPARECCHIATURE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data documento
              </label>
              <input
                type="date"
                value={uploadForm.dataDocumento}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    dataDocumento: e.target.value,
                  }))
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data scadenza
              </label>
              <input
                type="date"
                value={uploadForm.dataScadenza}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    dataScadenza: e.target.value,
                  }))
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note
            </label>
            <textarea
              value={uploadForm.descrizione}
              onChange={(e) =>
                setUploadForm((prev) => ({
                  ...prev,
                  descrizione: e.target.value,
                }))
              }
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Link Existing Document Modal */}
      <Modal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        title="Collega documento esistente"
      >
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Cerca documento per nome..."
            value={linkSearch}
            onChange={(e) => setLinkSearch(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
          />

          {loadingAvailableDocs ? (
            <div className="flex justify-center py-8">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-1">
              {availableDocs
                .filter(
                  (d) =>
                    !linkSearch ||
                    d.nome?.toLowerCase().includes(linkSearch.toLowerCase()),
                )
                .map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer group"
                    onClick={() => handleLinkDocument(doc.id)}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.nome}
                      </p>
                      <p className="text-xs text-gray-400">
                        {TIPO_DOCUMENTO_LABELS[doc.tipoDocumento] ||
                          "Documento"}
                        {doc.dataDocumento &&
                          ` - ${new Date(doc.dataDocumento).toLocaleDateString("it-IT")}`}
                      </p>
                    </div>
                    <span className="text-xs text-primary-600 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-2">
                      Collega
                    </span>
                  </div>
                ))}
              {availableDocs.filter(
                (d) =>
                  !linkSearch ||
                  d.nome?.toLowerCase().includes(linkSearch.toLowerCase()),
              ).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  {linkSearch
                    ? "Nessun documento trovato"
                    : "Nessun documento disponibile per questa RSA"}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Conferma eliminazione"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sei sicuro di voler eliminare l'apparecchiatura{" "}
            <strong>{apparecchiatura.nome}</strong>?
          </p>
          <p className="text-sm text-red-600">
            Questa azione non puo essere annullata.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Eliminazione..." : "Elimina"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DettaglioApparecchiaturaPage;
